import React from 'react';
import { Button } from '../ui/button';
import { ArrowRight, ChevronRight } from 'lucide-react';
import { useScrollAnimation } from '../../hooks/use-scroll-animation';

export const HeroSection = () => {
  const [badgeRef, badgeVisible] = useScrollAnimation({ threshold: 0.2 });
  const [headlineRef, headlineVisible] = useScrollAnimation({ threshold: 0.2 });
  const [subheadlineRef, subheadlineVisible] = useScrollAnimation({ threshold: 0.2 });
  const [ctaRef, ctaVisible] = useScrollAnimation({ threshold: 0.2 });

  return (
    <section className="relative min-h-screen flex items-center pt-24 lg:pt-0 overflow-hidden">
      {/* Premium background with gradients */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-slate-50 pointer-events-none" />
      <div className="absolute inset-0 opacity-[0.02] pointer-events-none" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
      }} />
      
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-40 z-10">
        <div className="max-w-4xl relative z-10">
          {/* Premium Badge */}
          <div 
            ref={badgeRef}
            className={`inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-gradient-to-r from-slate-100 to-slate-50 border border-slate-200/80 shadow-premium mb-10 backdrop-blur-sm transition-all duration-700 ${
              badgeVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <span className="relative w-2.5 h-2.5 rounded-full bg-gradient-to-r from-amber-500 to-amber-400">
              <span className="absolute inset-0 rounded-full bg-amber-400 animate-ping opacity-75" />
            </span>
            <span className="text-sm text-slate-700 font-semibold tracking-wide">Tech Services + Ventures</span>
          </div>

          {/* Premium Headline */}
          <h1 
            ref={headlineRef}
            className={`text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-extrabold text-slate-900 leading-[1.1] tracking-tight mb-8 transition-all duration-700 delay-100 ${
              headlineVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            Building Products.
            <br />
            <span className="text-slate-600">Scaling Platforms.</span>
            <br />
            <span className="bg-gradient-to-r from-slate-900 via-slate-700 via-amber-600/20 to-slate-500 bg-clip-text text-transparent">
              Launching Ventures.
            </span>
          </h1>

          {/* Premium Subheadline */}
          <p 
            ref={subheadlineRef}
            className={`text-xl sm:text-2xl text-slate-600 leading-relaxed max-w-2xl mb-12 font-medium transition-all duration-700 delay-200 ${
              subheadlineVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            Founder-led tech services and startup studio helping businesses build and scale digital products.
          </p>

          {/* Premium CTAs */}
          <div 
            ref={ctaRef}
            className={`flex flex-col sm:flex-row gap-5 transition-all duration-700 delay-300 relative z-20 ${
              ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <Button
              size="lg"
              className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 hover:from-slate-800 hover:via-slate-700 hover:to-slate-800 text-white px-10 py-7 text-base font-semibold group shadow-premium-lg hover:shadow-premium-xl transition-all duration-300 hover:scale-105"
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Start Your Project
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="border-2 border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 px-10 py-7 text-base font-semibold group shadow-premium hover:shadow-premium-lg transition-all duration-300 hover:scale-105 bg-white/80 backdrop-blur-sm"
              onClick={() => document.getElementById('ventures')?.scrollIntoView({ behavior: 'smooth' })}
            >
              See Our Work
              <ChevronRight className="ml-1 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
            </Button>
          </div>

          {/* Premium Trust indicators */}
          <div className="mt-20 pt-12 border-t border-slate-200/60 animate-fade-in">
            <p className="text-sm text-slate-500 mb-6 font-semibold tracking-wide uppercase">Trusted by startups and businesses</p>
            <div className="flex flex-wrap items-center gap-10">
              {['Startups', 'SMEs', 'Product Teams', 'Founders'].map((item, index) => (
                <div key={index} className="flex items-center gap-3 group cursor-default">
                  <div className="relative w-2.5 h-2.5 rounded-full bg-gradient-to-r from-amber-500 to-amber-400 group-hover:scale-125 transition-transform duration-300">
                    <div className="absolute inset-0 rounded-full bg-amber-400/50 animate-pulse" />
                  </div>
                  <span className="text-slate-500 text-sm font-semibold group-hover:text-slate-700 transition-colors">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Premium Decorative elements */}
      <div className="absolute top-1/4 right-0 w-[500px] h-[500px] bg-gradient-to-br from-amber-100/30 via-slate-100/40 to-transparent rounded-full blur-3xl opacity-60 animate-float-slow pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-gradient-to-br from-slate-200/30 to-amber-50/20 rounded-full blur-3xl opacity-50 animate-float pointer-events-none" />
      <div className="absolute top-1/2 left-0 w-96 h-96 bg-slate-100/20 rounded-full blur-3xl opacity-40 animate-pulse-slow pointer-events-none" />
    </section>
  );
};
